import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Link, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";

import { GraphqlErrorMessage } from "../../../components/ErrorMessage";
import { Loading } from "../../../components/LoadingFork";
import { withInstallation } from "../../../components/InstallationContext";
import { T } from "../constants";
import Panel from "../components/Panel";
import Plate from "../components/Plate";
import VipPageShell, { HeaderCount } from "../vip/components/VipPageShell";
import { formatDateTime, formatShortDate, statusLabel } from "../vip-booking/data";
import { alertLogsQuery } from "./queries";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 350;

const useStyles = makeStyles({
  head: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    borderBottom: `1px solid ${T.borderSoft}`,
    background: T.surface,
    padding: "12px 20px",
  },
  title: { margin: 0, fontSize: 16, fontWeight: 600, color: T.fg },
  subtitle: { margin: "4px 0 0", fontSize: 12, color: T.mutedFg },
  filters: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 },
  field: {
    border: `1px solid ${T.border}`,
    borderRadius: 6,
    color: T.fg,
    fontFamily: "inherit",
    fontSize: 13,
    padding: "8px 10px",
  },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 1280 },
  th: {
    borderBottom: `1px solid ${T.borderSoft}`,
    color: T.mutedFg,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.08em",
    padding: "12px 16px",
    textAlign: "left",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  td: {
    borderBottom: `1px solid ${T.borderSoft}`,
    color: T.fg,
    fontSize: 13,
    padding: "14px 16px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  rowLink: {
    cursor: "pointer",
    "&:hover $td": { background: T.primaryTint },
  },
  status: {
    borderRadius: 6,
    background: T.warningTint,
    color: T.warningFg,
    display: "inline-block",
    fontSize: 11,
    fontWeight: 700,
    padding: "4px 8px",
  },
  alertStatus: {
    borderRadius: 6,
    display: "inline-block",
    fontSize: 11,
    fontWeight: 700,
    padding: "4px 8px",
  },
  alertOk: { background: T.successTint, color: T.successText },
  alertError: { background: T.dangerTint, color: T.danger },
  typeChip: {
    border: `1px solid ${T.border}`,
    borderRadius: 6,
    color: T.mutedFg,
    display: "inline-block",
    fontSize: 11,
    fontWeight: 700,
    padding: "4px 8px",
  },
  team: { color: T.mutedFg, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis" },
  teamLink: { color: "inherit", textDecoration: "none" },
  empty: { color: T.mutedFg, fontSize: 14, padding: 20, textAlign: "center" },
  footer: { borderTop: `1px solid ${T.borderSoft}`, padding: "12px 20px", textAlign: "center" },
  loadMore: {
    border: `1px solid ${T.border}`,
    borderRadius: 6,
    background: T.elevated,
    color: T.fg,
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 13,
    fontWeight: 600,
    padding: "8px 14px",
  },
});

function statusTone(status) {
  return status === "FAILED" ? "error" : "success";
}

function VipAlerts({ match, installation }) {
  const s = useStyles();
  const history = useHistory();
  const conciergeUrl = match.url.replace(/\/vip-alerts$/, "");
  const installationId = installation?.id;
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [providerType, setProviderType] = useState("all");
  const [status, setStatus] = useState("all");
  const [limit, setLimit] = useState(PAGE_SIZE);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [search]);

  const { data, previousData, loading, error } = useQuery(alertLogsQuery, {
    variables: {
      installationId,
      search: debouncedSearch || null,
      offset: 0,
      limit,
    },
    skip: !installationId,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const activeData = data || previousData;
  const response = activeData?.alertLogs;
  const alerts = response?.items || [];
  const filteredAlerts = useMemo(
    () =>
      alerts.filter((alert) => {
        if (providerType !== "all" && alert.providerType !== providerType) return false;
        if (status !== "all" && alert.status !== status) return false;
        return true;
      }),
    [alerts, providerType, status],
  );
  const totalCount = response?.count || 0;
  const canLoadMore = alerts.length < totalCount;

  if (loading && !response) return <Loading />;

  return (
    <VipPageShell
      eyebrow="Concierge console - Audit"
      title="VIP Alert Logs"
      right={<HeaderCount label="Rows" value={totalCount} />}
    >
      {error ? <GraphqlErrorMessage error={error} /> : null}
      <Panel>
        <div className={s.head}>
          <div>
            <h2 className={s.title}>Dispatched VIP alerts</h2>
            <p className={s.subtitle}>One row per SMS or email delivery attempt.</p>
          </div>
          <div className={s.filters}>
            <input
              className={s.field}
              value={search}
              onChange={(event) => {
                setLimit(PAGE_SIZE);
                setSearch(event.target.value);
              }}
              placeholder="Search plate, guest, card"
            />
            <select aria-label="Filter by alert type" className={s.field} value={providerType} onChange={(event) => setProviderType(event.target.value)}>
              <option value="all">All types</option>
              <option value="SMS">SMS</option>
              <option value="EMAIL">Email</option>
            </select>
            <select aria-label="Filter by alert status" className={s.field} value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="all">All statuses</option>
              <option value="DELIVERED">Delivered</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>
        </div>
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th className={s.th}>Team notified</th>
                <th className={s.th}>Alert type</th>
                <th className={s.th}>Alert created at</th>
                <th className={s.th}>Plate</th>
                <th className={s.th}>Guest name</th>
                <th className={s.th}>Booking start day</th>
                <th className={s.th}>End day</th>
                <th className={s.th}>Guest card name</th>
                <th className={s.th}>Booking status</th>
                <th className={s.th}>Alert status</th>
              </tr>
            </thead>
            <tbody>
              {filteredAlerts.map((alert) => {
                const bookingUrl = `${conciergeUrl}/vip-booking/${alert.booking?.id || alert.bookingId}`;
                const canOpenBooking = !alert.sanctionedPlateId && Boolean(alert.booking?.id || alert.bookingId);

                return (
                <tr
                  key={alert.id}
                  className={canOpenBooking ? s.rowLink : undefined}
                  role={canOpenBooking ? "link" : undefined}
                  tabIndex={canOpenBooking ? 0 : undefined}
                  aria-label={canOpenBooking ? `Open booking for ${alert.booking?.guestName || alert.bookingId}` : undefined}
                  onClick={canOpenBooking ? () => history.push(bookingUrl) : undefined}
                  onKeyDown={canOpenBooking ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      history.push(bookingUrl);
                    }
                  } : undefined}
                >
                  <td className={`${s.td} ${s.team}`}>
                    {canOpenBooking ? (
                      <Link to={bookingUrl} className={s.teamLink} onClick={(event) => event.stopPropagation()}>
                        {alert.contact?.name || "-"}
                      </Link>
                    ) : alert.contact?.name || "-"}
                  </td>
                  <td className={s.td}><span className={s.typeChip}>{alert.providerType}</span></td>
                  <td className={s.td}>{formatDateTime(alert.createdAt)}</td>
                  <td className={s.td}><Plate>{alert.booking?.plate || alert.sanctionedPlate?.plate || "-"}</Plate></td>
                  <td className={s.td}>{alert.booking?.guestName || "-"}</td>
                  <td className={s.td}>{formatShortDate(alert.booking?.startDay)}</td>
                  <td className={s.td}>{formatShortDate(alert.booking?.endDay)}</td>
                  <td className={s.td}>{alert.booking?.rateCard?.name || "-"}</td>
                  <td className={s.td}><span className={s.status}>{alert.booking?.status || "-"}</span></td>
                  <td className={s.td}>
                    <span className={`${s.alertStatus} ${statusTone(alert.status) === "error" ? s.alertError : s.alertOk}`}>
                      {statusLabel(alert.status)}
                    </span>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!filteredAlerts.length ? <div className={s.empty}>{loading ? "Loading alerts..." : "No VIP alert logs found."}</div> : null}
        {canLoadMore ? (
          <div className={s.footer}>
            <button type="button" className={s.loadMore} onClick={() => setLimit((value) => value + PAGE_SIZE)} disabled={loading}>
              {loading ? "Loading..." : "Load more"}
            </button>
          </div>
        ) : null}
      </Panel>
    </VipPageShell>
  );
}

export default withInstallation(VipAlerts);
